// Escrow public key bundled in the SPA (D-45, COF-04, CRY-05).
// Phase 1: single-key break-glass; Phase 6 swaps to Shamir 3-of-5 with hardware tokens.
// Private key NEVER leaves the YubiKey/HSM in production.

let cachedJwk: JsonWebKey | null = null;
let devKeypairPromise: Promise<{ public: JsonWebKey; private: JsonWebKey }> | null = null;

/**
 * Returns the escrow public JWK.
 *
 * Production: read from `VITE_ESCROW_PUBLIC_KEY_JWK` (set at build time per env).
 * Dev fallback: generate an ephemeral RSA-OAEP-2048 keypair on first call,
 * stash it in module memory, log a loud warning. The key resets on every page
 * reload — denúncias submitted in dev fallback mode become unrecoverable, which
 * is the correct dev semantics (don't accidentally rely on dev escrow).
 */
export async function getEscrowPublicJwk(): Promise<JsonWebKey> {
  if (cachedJwk) return cachedJwk;

  const fromEnv = import.meta.env.VITE_ESCROW_PUBLIC_KEY_JWK;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    cachedJwk = JSON.parse(fromEnv) as JsonWebKey;
    return cachedJwk;
  }

  if (import.meta.env.MODE === 'production') {
    throw new Error(
      '[escrow] VITE_ESCROW_PUBLIC_KEY_JWK is required in production. ' +
        'See Phase 6 cofre runbook for key provisioning.',
    );
  }

  // Dev-only fallback. Loud warning so this never silently ships.
  console.warn(
    '[escrow] DEV FALLBACK ACTIVE — generating ephemeral RSA-OAEP-2048 keypair. ' +
      'Set VITE_ESCROW_PUBLIC_KEY_JWK in `.env.local` for stable dev escrow. ' +
      'Submissions encrypted with this key cannot be recovered after page reload.',
  );

  if (!devKeypairPromise) {
    devKeypairPromise = generateDevKeypair();
  }
  const pair = await devKeypairPromise;
  cachedJwk = pair.public;
  return cachedJwk;
}

async function generateDevKeypair(): Promise<{ public: JsonWebKey; private: JsonWebKey }> {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['wrapKey', 'unwrapKey'],
  );
  const pub = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
  const priv = await crypto.subtle.exportKey('jwk', keyPair.privateKey);
  return { public: pub, private: priv };
}

/** TEST-ONLY: clear cached JWK between tests. */
export function _resetEscrowCache(): void {
  cachedJwk = null;
  devKeypairPromise = null;
}
